import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from '../lib/theme';
import {
  listEmailQueue,
  getEmailQueueStats,
  getInboundAddress,
  retryEmail,
  deleteEmail,
} from '../lib/api';
import ForwardingAddressCard from '../components/inbox/ForwardingAddressCard';
import EmailQueueList from '../components/inbox/EmailQueueList';

const TABS = [
  { key: 'failed',    label: 'Failed' },
  { key: 'succeeded', label: 'Succeeded' },
  { key: 'all',       label: 'All' },
];

export default function Inbox() {
  const { colors } = useTheme();
  const [tab, setTab] = useState('failed');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [address, setAddress] = useState('');
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      const [lst, st, addr] = await Promise.all([
        listEmailQueue(tab, 100, 0),
        getEmailQueueStats(),
        getInboundAddress(),
      ]);
      setItems(lst.items);
      setStats(st);
      setAddress(addr.address || '');
    } catch (err) {
      console.error('Failed to load inbox data:', err);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const onRetry = async (id) => {
    await retryEmail(id);
    setToast('Retry queued');
    setTimeout(() => setToast(null), 1500);
    load();
  };

  const onDismiss = async (id) => {
    if (!window.confirm('Dismiss this email? The linked receipt (if any) will remain.')) return;
    await deleteEmail(id);
    load();
  };

  const tabCount = (k) => stats ? (k === 'all' ? stats.total : (stats[k] ?? 0)) : 0;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="inbox-screen">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: colors.text }}>Email Inbox</h1>
        <p className="text-sm mt-1" style={{ color: colors.textMuted }}>
          Emails you forward to your inbound address show up here. Failed extractions can be retried or dismissed.
        </p>
      </div>

      <ForwardingAddressCard address={address} colors={colors} />

      {/* Tabs */}
      <div className="flex gap-2" data-testid="inbox-tabs">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button key={t.key} data-testid={`inbox-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className="px-4 py-2 rounded-full text-sm font-semibold border"
              style={{
                backgroundColor: active ? colors.accent : colors.surface,
                color: active ? '#FFF' : colors.textMuted,
                borderColor: active ? colors.accent : colors.border,
              }}>
              {t.label} · {tabCount(t.key)}
            </button>
          );
        })}
      </div>

      <EmailQueueList
        items={items}
        tab={tab}
        onRetry={onRetry}
        onDismiss={onDismiss}
        colors={colors}
      />

      {toast ? (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-xs font-semibold text-white"
          style={{ backgroundColor: colors.text }}>{toast}</div>
      ) : null}
    </div>
  );
}
